import { AddToCartButtonPrimary } from 'components/Pages/ProductDetail/ProductDetailAddToCart/AddToCartButtonPrimary';
import { mapAddToCartPopupData } from 'connectors/cart/Cart';
import { useDomainConfig } from 'context/DomainConfigProvider/DomainConfigProvider';
import { groupProductsInDrawer } from 'helpers/configurator/configuratorHelpers';
import { useAddToCartSimple } from 'hooks/cart/useAddToCartSimple';
import useDebounce from 'hooks/helpers/UseDebounce';
import dynamic from 'next/dynamic';
import { useCallback, useMemo } from 'react';
import { usePersistStore } from 'store/usePersistStore';
import { useSessionStore } from 'store/useSessionStore';
import { AddToCartPopupDataType } from 'types/cart';
import { ConfiguratorProductTypeSummed } from 'types/configurator';

const AddToCartPopup = dynamic(
    () =>
        import('components/Pages/Configurator/Popup/AddToCart/AddToCartPopup').then(
            (component) => component.AddToCartPopup,
        ),
    {
        ssr: false,
    },
);

export const AddToCartButton = () => {
    const { productsInDrawer } = usePersistStore();

    const productsInDrawerSummed: ConfiguratorProductTypeSummed[] = useMemo(() => {
        return groupProductsInDrawer(productsInDrawer);
    }, [productsInDrawer]);

    const [changeCartItemQuantity, isAddToCartFetching] = useAddToCartSimple();
    const { updateCartUuid, cartUuid } = usePersistStore((s) => s);
    const updatePortalContent = useSessionStore((s) => s.updatePortalContent);

    const isAddToCartFetchingDebounced = useDebounce(isAddToCartFetching, 200);
    const { currencyCode } = useDomainConfig();

    const onAddToCart = useCallback(async () => {
        const addToCartResponses: AddToCartPopupDataType[] = [];
        const addToCartFailures: string[] = [];
        let lastCartUuid = cartUuid;
        let totalProductsAdded = 0;

        for (let index = 0; index < productsInDrawerSummed.length; index++) {
            const product = productsInDrawerSummed[index];

            const addToCartResult = await changeCartItemQuantity(
                product.uuid,
                index,
                product.quantity,
                'flexi config',
                lastCartUuid,
                false,
            );
            const mappedAddToCartResult = mapAddToCartPopupData(addToCartResult, currencyCode);

            if (mappedAddToCartResult) {
                totalProductsAdded += product.quantity;
                addToCartResponses.push(mappedAddToCartResult);
                lastCartUuid = addToCartResult?.cart.uuid ?? lastCartUuid;
            } else {
                addToCartFailures.push(product.uuid);
            }
        }

        if (lastCartUuid) {
            updateCartUuid(lastCartUuid);
        }

        updatePortalContent(
            <AddToCartPopup
                availableProducts={addToCartResponses}
                productsQuantityAddedTotal={totalProductsAdded}
                unavailableProducts={addToCartFailures}
                onCloseCallback={() => updatePortalContent(null)}
            />,
        );
    }, [productsInDrawerSummed, changeCartItemQuantity, currencyCode, updateCartUuid, cartUuid]);

    return (
        <>
            <AddToCartButtonPrimary
                className="h-10 px-4 text-h6 vl:px-5"
                isBookingOnly={false}
                isDisabled={productsInDrawer.length === 0}
                isLoading={isAddToCartFetching || isAddToCartFetchingDebounced}
                isResponsive={false}
                onClick={onAddToCart}
            />
        </>
    );
};
